import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { TaxPayerType } from './types'
import {Button} from "@/components/ui/button.tsx";
import {AlertCircle, DollarSign, FileText, MapPin, Plus, Search} from "lucide-react";
import {Card} from "@/components/ui/card.tsx";
import { Input } from '@/components/ui/input'
import Pagination from '@/components/Pagination'

interface IndexProps {
  tax_payers: TaxPayerType[]
  pagination: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export default function Index({ tax_payers, pagination }: IndexProps) {
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('q') ?? ''
  })
  const [page, setPage] = useState(() => {
    if (typeof window === 'undefined') return 1
    return Number(new URLSearchParams(window.location.search).get('page')) || 1
  })
  const perPage = pagination?.per_page || 10

  useEffect(() => {
    const timeout = setTimeout(() => {
      router.get(
        '/tax_payers',
        {
          q: searchTerm.trim() || undefined,
          page,
          per_page: perPage,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['tax_payers', 'pagination'],
        }
      )
    }, 300)

    return () => clearTimeout(timeout)
  }, [searchTerm, page, perPage])

  return (
    <>
      <Head title="Tax payers" />
      <div className="flex flex-col gap-6 p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Tax Type Management</h2>
            <p className="text-muted-foreground">Configure tax types, rates, and collection rules</p>
          </div>
          <Link href="/tax_payers/new">
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Add Tax Type
            </Button>
          </Link>
        </div>

        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Taxpayers</p>
                <p className="mt-2 text-3xl font-bold text-foreground">{pagination.total}</p>
              </div>
              <FileText className="h-8 w-8 text-primary" />
            </div>
          </Card>

          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Outstanding Balance</p>
                <p className="mt-2 text-3xl font-bold text-warning">0</p>
              </div>
              <DollarSign className="h-8 w-8 text-warning" />
            </div>
          </Card>

          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Overdue</p>
                <p className="mt-2 text-3xl font-bold text-destructive">0</p>
              </div>
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
          </Card>

          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Paid Up</p>
                <p className="mt-2 text-3xl font-bold text-success">0</p>
              </div>
              <FileText className="h-8 w-8 text-success" />
            </div>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="border-border bg-card p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                  className="pl-10"
                  placeholder="Search by name, fullname, NIF, or RCCM"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value)
                    setPage(1)
                  }}
              />
            </div>
            <Button variant="outline">Filter</Button>
          </div>
        </Card>

        {/* Taxpayers Table */}
        <Card className="border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border">
              <tr className="text-left">
                <th className="p-4 text-sm font-medium text-muted-foreground">Taxpayer</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Fiscal ID</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Area</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Category</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Address</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Balance</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
              </thead>
              <tbody>
              {tax_payers.map((taxpayer) => (
                  <tr key={taxpayer.id} className="border-b border-border transition-colors hover:bg-secondary/50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-foreground">{taxpayer.name}</p>
                        <p className="text-sm text-muted-foreground">{taxpayer.phone}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="font-mono text-sm text-foreground">{taxpayer.nif}</p>
                    </td>
                    <td className="p-4">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                      <MapPin className="h-3 w-3" />
                      {taxpayer.area?.name || taxpayer.area_id}
                    </span>
                    </td>
                    <td className="p-4">
                    <span className="inline-flex rounded-full bg-accent/10 px-2.5 py-1 text-xs font-medium text-accent">
                      {taxpayer.category?.name || taxpayer.category_id}
                    </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-foreground">{taxpayer.address}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <p
                          className={`font-mono text-sm font-semibold ${
                              20 > 0 ? "text-warning" : "text-success"
                          }`}
                      >
                        0 XOF
                      </p>
                    </td>
                    <td className="p-4">
                    <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                            taxpayer.status === "paid"
                                ? "bg-success/10 text-success"
                                : taxpayer.status === "overdue"
                                    ? "bg-destructive/10 text-destructive"
                                    : "bg-warning/10 text-warning"
                        }`}
                    >
                      {taxpayer.status}
                    </span>
                    </td>
                    <td className="p-4">
                      <Link href={`/tax_payers/${taxpayer.id}`}>
                        <button className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">View Details</button>
                      </Link>
                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Pagination
          page={pagination.page}
          totalPages={pagination.total_pages}
          total={pagination.total}
          onPageChange={(nextPage) => setPage(nextPage)}
        />

      </div>
    </>
  )
}
